import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  InputAdornment,
} from '@material-ui/core';
import { Cancel, Sms } from '@material-ui/icons';
import { AxiosError } from 'axios';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { IRealEstateModel } from 'interfaces';
import useError from 'components/ErrorProvider/useError';
import { FormikTextFieldComponent } from 'components/Field';
import { clearPhoneNumberFormat } from 'helper';
import { IBigAppDownloadLink } from './interfaces';
import { sendBigAppDownloadLink } from './api';

const BIG_APP_DEFAULT_URL = 'app.bi.group/cz';

interface IProps {
  realEstate: IRealEstateModel | null;
  phoneNumber: string;
  onClose(): void;
}

const SendBigAppDownloadLinkDialog: React.FC<IProps & DialogProps> = ({
  open,
  realEstate,
  phoneNumber = '',
  onClose,
}) => {
  // Local state
  const [loading, setLoading] = useState(false);

  // Global notification
  const { addError } = useError();
  const { enqueueSnackbar } = useSnackbar();
  const PHONE_PREFIX = realEstate?.phonePrefix ?? '+7';
  const PHONE_MASK = realEstate?.phoneMask ?? '(###) ### ## ##';

  const initialValues: IBigAppDownloadLink = {
    phoneNumber,
    link: BIG_APP_DEFAULT_URL,
  };

  const validationSchema = yup.object().shape({
    phoneNumber: yup.string().required('Необходимо ввести номер телефона').min(15, 'Введите корректный номер телефона'),
    link: yup.string().required('Обязательное поле'),
  });

  const handleSubmitPhone = async (values: IBigAppDownloadLink) => {
    const { phoneNumber, link } = values;
    setLoading(true);

    try {
      await sendBigAppDownloadLink({
        phoneNumber: `${PHONE_PREFIX}` + clearPhoneNumberFormat(phoneNumber, PHONE_MASK),
        link: 'https://' + link,
      });

      enqueueSnackbar('Сообщение было успешно доставлено!', { variant: 'success' });
      onClose();
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle>
        <b>Отправить СМС на номер</b>
      </DialogTitle>

      <Formik
        onSubmit={handleSubmitPhone}
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
      >
        {() => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    name="phoneNumber"
                    label="Номер телефона"
                    mask={PHONE_MASK}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{PHONE_PREFIX}</InputAdornment>,
                    }}
                    disabled={loading}
                    component={FormikTextFieldComponent}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    name="link"
                    label="Ссылка на скачивание"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">https://</InputAdornment>,
                    }}
                    disabled
                    component={FormikTextFieldComponent}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button variant="contained" disabled={loading} onClick={onClose} startIcon={<Cancel />}>
                Отменить
              </Button>

              <Button color="primary" variant="contained" disabled={loading} type="submit" startIcon={<Sms />}>
                Отправить
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default SendBigAppDownloadLinkDialog;
